/**
 * Trustee-side primitives: partial decryption with ZK proof of correctness.
 * See ADR 0008 § 2 + § 7 and Belenios v2 specification § 5.
 *
 * Each of the (at least `threshold`) cooperating trustees produces one
 * `PartialDecryption` over the homomorphically-aggregated tally
 * ciphertexts of the ballot — one decryption share + ZK proof per
 * option. The verifier CLI combines them with Lagrange-in-exponent and
 * checks every ZK proof.
 *
 * STATUS: M5 (partial decryption). The trustee's secret share never
 * leaves this scope; for binding scrutins post-pilot, `partialDecrypt`
 * is the surface that wraps a hardware-backed key store call.
 */
import { ristretto255 } from "@noble/curves/ed25519";

import type {
  ChaumPedersenProof,
  ElGamalCiphertext,
  PartialDecryption,
  RistrettoPoint,
  TrusteeSecretShare,
} from "../types";
import {
  concatBytes,
  encodePoint,
} from "../internal/encoding";
import {
  chaumPedersenProve,
  chaumPedersenVerify,
} from "../internal/sigma";

const G = ristretto255.Point.BASE;
const TAG_PARTIAL = new TextEncoder().encode("OAR/voting/partial/v1\0");

function partialContext(
  ballotId: string,
  trusteeId: string,
  optionIndex: number,
): Uint8Array {
  const u32 = new Uint8Array(4);
  new DataView(u32.buffer).setUint32(0, optionIndex, false);
  const lpStr = (s: string): Uint8Array => {
    const b = new TextEncoder().encode(s);
    const len = new Uint8Array(4);
    new DataView(len.buffer).setUint32(0, b.length, false);
    return concatBytes(len, b);
  };
  return concatBytes(TAG_PARTIAL, lpStr(ballotId), lpStr(trusteeId), u32);
}

export interface PartialDecryptInput {
  readonly ballotId: string;
  /**
   * Per-option aggregate ciphertexts. The trustee never sees individual
   * voters' ballots — the bulletin orchestrator computes the homomorphic
   * sum (revote-superseded entries excluded) and passes only the totals.
   */
  readonly aggregate: ReadonlyArray<ElGamalCiphertext>;
  /** Trustee's secret share (held only on this trustee's device). */
  readonly trusteeShare: TrusteeSecretShare;
  /** Trustee's verification key from the DKG transcript (for binding). */
  readonly verificationKey: RistrettoPoint;
}

export function partialDecrypt(input: PartialDecryptInput): PartialDecryption {
  const x = input.trusteeShare.secret;
  const perOption = input.aggregate.map((agg, i) => {
    const decryptionShare = agg.c1.multiply(x);
    const zkProof: ChaumPedersenProof = chaumPedersenProve(
      x,
      G,
      input.verificationKey,
      agg.c1,
      decryptionShare,
      partialContext(input.ballotId, input.trusteeShare.trusteeId, i),
    );
    return { decryptionShare, zkProof };
  });
  return { trusteeId: input.trusteeShare.trusteeId, perOption };
}

export interface VerifyPartialInput {
  readonly ballotId: string;
  readonly partial: PartialDecryption;
  readonly aggregate: ReadonlyArray<ElGamalCiphertext>;
  /** Trustee's verification key from the DKG transcript. */
  readonly verificationKey: RistrettoPoint;
}

export interface PartialVerifyResult {
  readonly valid: boolean;
  readonly errors: ReadonlyArray<string>;
}

/**
 * Verify every per-option Chaum-Pedersen proof in `partial` against
 * the trustee's verificationKey. Pure function; called by tally
 * combiner and by the verifier.
 */
export function verifyPartialDecryption(
  input: VerifyPartialInput,
): PartialVerifyResult {
  const errors: string[] = [];
  if (input.partial.perOption.length !== input.aggregate.length) {
    errors.push(
      `perOption length ${input.partial.perOption.length} != aggregate length ${input.aggregate.length}`,
    );
  }
  const n = Math.min(input.partial.perOption.length, input.aggregate.length);
  for (let i = 0; i < n; i++) {
    const item = input.partial.perOption[i]!;
    const ok = chaumPedersenVerify(
      item.zkProof,
      G,
      input.verificationKey,
      input.aggregate[i]!.c1,
      item.decryptionShare,
      partialContext(input.ballotId, input.partial.trusteeId, i),
    );
    if (!ok) errors.push(`per-option ${i} ZK proof failed`);
  }
  return { valid: errors.length === 0, errors };
}
